import { ApiError } from '../../lib/api/client';
import { useAlertEvidence } from '../../hooks/useAlertEvidence';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { AlertEvidenceViewer } from './AlertEvidenceViewer';

interface AlertEvidenceListProps {
  alertId: string;
  /** Prefijo del nombre del archivo si se descarga el video, p. ej. `evidencia-pat001`. */
  fileStem?: string;
}

// `PRODUCT.md` §4.3/§4.8: descargar evidencia es de Supervisor, Ejecutivo y
// Administrador. El Operador SÍ ve el video de sus propios incidentes, pero el
// backend le responde 403 a la descarga: no se le ofrece un botón que solo
// puede fallar.
const ROLES_THAT_DOWNLOAD = ['admin', 'supervisor', 'executive'];

// Un 403, un 501 y un fallo de red son cosas distintas de "esta alerta no tiene
// video": aplastarlas todas en "sin evidencia" (lo que hacía esto antes) hace
// pasar un permiso faltante o un servicio caído por ausencia de dato.
function describeLoadError(err: Error): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return 'Tu rol no tiene permiso para ver la evidencia de esta alerta.';
    if (err.status === 404) return 'No se encontró la evidencia de esta alerta.';
    if (err.status === 501) return 'El archivo de video no está configurado en este entorno.';
    return err.detail ? `${err.title}: ${err.detail}` : err.title;
  }
  return 'No se pudo cargar la evidencia de esta alerta.';
}

export function AlertEvidenceList({ alertId, fileStem = 'evidencia' }: AlertEvidenceListProps) {
  const { user } = useAuth();
  const { evidence, isLoading, error, reload } = useAlertEvidence(alertId);

  if (isLoading && !evidence) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem' }}>
        <Spinner size="sm" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--bg-raised)', borderRadius: 'var(--radius-lg)', fontSize: '12.5px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
        <span>{describeLoadError(error)}</span>
        {!(error instanceof ApiError && (error.status === 403 || error.status === 404)) && (
          <Button size="sm" variant="ghost" onClick={() => void reload()}>Reintentar</Button>
        )}
      </div>
    );
  }

  if (!evidence) return null;

  return (
    <AlertEvidenceViewer
      evidence={evidence}
      canDownload={!!user && ROLES_THAT_DOWNLOAD.includes(user.role)}
      fileStem={fileStem}
    />
  );
}
